import React, {useEffect, useState} from 'react';
import type {Key} from '@react-types/shared';
import type {View} from '@sail/engine';
import {createView, view} from '@sail/engine';
import {useIntl} from '@sail/intl';
import {Tab, TabList, TabPanel, TabPanels, Tabs} from '../tabs/Tabs';
import {bottomSheetDefaultTabTitle, bottomSheetTabsAriaLabel} from './messages';
import type {DrawerSize} from './BaseDrawer';
import {isReactNodeEmptyConsideringAnimatePresence} from '../../lib/isReactNodeEmptyConsideringAnimatePresence';
import {BottomSheetTabContent} from './BottomSheetTabContent';

type BottomSheetContentTabsProps = {
  setIsScrolled: (isScrolled: boolean) => void;
  setIsOverflowing: (isOverflowing: boolean) => void;
  contentWrapperRef: React.RefObject<HTMLDivElement>;
  size: DrawerSize;
  start: React.ReactNode;
  children: React.ReactNode;
  end: React.ReactNode;
  bottomSheetStartTabTitle: React.ReactNode;
  bottomSheetChildrenTabTitle: React.ReactNode;
  bottomSheetEndTabTitle: React.ReactNode;
  subviews?: View.Subviews<{
    content: 'div';
  }>;
};

function useGeneratedTabs({
  start,
  children,
  end,
  bottomSheetStartTabTitle,
  bottomSheetChildrenTabTitle,
  bottomSheetEndTabTitle,
}: {
  start: React.ReactNode;
  children: React.ReactNode;
  end: React.ReactNode;
  bottomSheetStartTabTitle: React.ReactNode;
  bottomSheetChildrenTabTitle: React.ReactNode;
  bottomSheetEndTabTitle: React.ReactNode;
}) {
  const intl = useIntl();
  const tabs: {
    key: string;
    title: React.ReactNode;
    content: React.ReactNode;
  }[] = [];

  const defaultTabTitle = () =>
    intl.formatMessage(bottomSheetDefaultTabTitle, {
      number: tabs.length + 1,
    });

  if (!isReactNodeEmptyConsideringAnimatePresence(start)) {
    tabs.push({
      key: 'start',
      title: bottomSheetStartTabTitle ?? defaultTabTitle(),
      content: start,
    });
  }
  if (!isReactNodeEmptyConsideringAnimatePresence(children)) {
    tabs.push({
      key: 'content',
      title: bottomSheetChildrenTabTitle ?? defaultTabTitle(),
      content: children,
    });
  }
  if (!isReactNodeEmptyConsideringAnimatePresence(end)) {
    tabs.push({
      key: 'end',
      title: bottomSheetEndTabTitle ?? defaultTabTitle(),
      content: end,
    });
  }

  return tabs;
}

export const BottomSheetContentTabs = createView<BottomSheetContentTabsProps>(
  ({
    setIsScrolled,
    setIsOverflowing,
    contentWrapperRef,
    size,
    start,
    children,
    end,
    bottomSheetStartTabTitle,
    bottomSheetChildrenTabTitle,
    bottomSheetEndTabTitle,
    subviews,
    ...props
  }: BottomSheetContentTabsProps) => {
    const intl = useIntl();
    const [selectedKey, setSelectedKey] = useState<Key>('content');

    const tabs = useGeneratedTabs({
      start,
      children,
      end,
      bottomSheetStartTabTitle,
      bottomSheetChildrenTabTitle,
      bottomSheetEndTabTitle,
    });

    useEffect(() => {
      if (tabs.length > 0 && !tabs.some((tab) => tab.key === selectedKey)) {
        // @ts-expect-error - Error after enabling noUncheckedIndexedAccess
        setSelectedKey(tabs[0].key);
      }
    }, [tabs, selectedKey]);

    if (tabs.length === 0) {
      return null;
    }

    const renderedTabContents = tabs.map((tab) => (
      <BottomSheetTabContent
        isSelected={tab.key === selectedKey}
        isShowingTabList={tabs.length > 1}
        setIsScrolled={setIsScrolled}
        setIsOverflowing={setIsOverflowing}
        contentWrapperRef={contentWrapperRef}
        size={size}
        inherits={tab.key === 'content' ? subviews?.content : undefined}
      >
        {tab.content}
      </BottomSheetTabContent>
    ));

    if (renderedTabContents.length === 1) {
      // If there is only one tab, we don't need to render the tab list.
      return (
        <view.div
          {...props}
          css={{
            stack: 'x',
            alignY: 'stretch',
            minHeight: 'space.0',
            height: 'fill',
          }}
        >
          {renderedTabContents[0]}
        </view.div>
      );
    }

    return (
      <Tabs
        {...props}
        fitted
        size="medium"
        onSelectionChange={setSelectedKey}
        selectedKey={selectedKey}
        css={{
          stack: 'y',
          minHeight: 'space.0',
          height: 'fill',
        }}
      >
        <TabList aria-label={intl.formatMessage(bottomSheetTabsAriaLabel)}>
          {tabs.map((tab) => (
            <Tab key={tab.key} id={tab.key}>
              {tab.title}
            </Tab>
          ))}
        </TabList>
        <TabPanels>
          {tabs.map((tab, index) => (
            <TabPanel
              key={tab.key}
              id={tab.key}
              css={{
                stack: 'x',
                alignY: 'stretch',
                minHeight: 'space.0',
                height: 'fill',
              }}
            >
              {renderedTabContents[index]}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    );
  },
);
